/* eslint-disable import/no-extraneous-dependencies */
import { FC, useContext, useMemo, useRef, useState } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { useTranslation } from "react-i18next";
import "./Reports.scss";
import { AccountContext } from "auth/accountProvider";

type EmbedStatus = "loading" | "ready" | "error";

const Reports: FC = () => {
  const { t } = useTranslation();
  const { userInfo } = useContext(AccountContext);

  const reportsBlockRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<EmbedStatus>("loading");

  const eventHandlers = useMemo(
    () =>
      new Map<string, () => void>([
        ["loaded", () => setStatus("ready")],
        ["rendered", () => setStatus("ready")],
        ["error", () => setStatus("error")],
      ]),
    []
  );

  const renderBody = () => {
    // Auth/user info still resolving.
    if (!userInfo) {
      return (
        <div className="reports__state" role="status" aria-live="polite">
          <span className="spinner spinner-default-grey" />
        </div>
      );
    }

    // No Power BI report provisioned for this account.
    if (!userInfo.reportId) {
      return (
        <div className="reports__state">
          <p className="reports__state-message">{t("reportsNoReport")}</p>
        </div>
      );
    }

    return (
      <>
        {status === "loading" && (
          <div className="reports__state" role="status" aria-live="polite">
            <span className="spinner spinner-default-grey" />
          </div>
        )}
        {status === "error" ? (
          <div className="reports__state" role="alert">
            <p className="reports__state-message">{t("reportsLoadError")}</p>
          </div>
        ) : (
          <PowerBIEmbed
            embedConfig={{
              type: "report",
              id: userInfo.reportId,
              embedUrl: userInfo.embedUrl,
              accessToken: userInfo.embedToken,
              tokenType: models.TokenType.Embed,
              settings: {
                filterPaneEnabled: false,
                navContentPaneEnabled: false,
                background: models.BackgroundType.Transparent,
                layoutType: models.LayoutType.Custom,
                customLayout: {
                  displayOption: models.DisplayOption.FitToWidth,
                },
              },
            }}
            eventHandlers={eventHandlers}
            cssClassName="reports__embed"
          />
        )}
      </>
    );
  };

  return (
    <div className="reports container">
      <h1>{t("reportsPageTitle")}</h1>
      <div ref={reportsBlockRef} className="reports__block">
        {renderBody()}
      </div>
    </div>
  );
};

export default Reports;
